/**
 * Enemy - Represents an enemy in the game world with AI behavior
 */
export default class Enemy {
  /**
   * Create a new enemy entity
   * @param {Object} config - Enemy configuration
   * @param {string} config.id - Unique enemy identifier
   * @param {Object} config.position - Position coordinates {x, y, z}
   * @param {string} config.type - Enemy type (melee, ranged, etc)
   * @param {number} config.health - Enemy health points
   * @param {number} config.level - Enemy level
   * @param {number} config.speed - Movement speed from server
   * @param {boolean} config.isBoss - Whether this enemy is a boss
   * @param {number} config.scale - Scale factor for the enemy model
   */
  constructor(config) {
    if (!config || !config.id) {
      console.error('Enemy created without required ID');
      throw new Error('Enemy requires a valid ID');
    }
    
    this.id = config.id;
    this.type = config.type || 'default';
    
    // Set position with defaults if not provided
    this.position = {
      x: 0,
      y: 0,
      z: 0,
      ...config.position
    };
    
    // CRITICAL FIX: Store initial spawn position to prevent any movement during freeze
    this.initialPosition = { ...this.position };
    
    // Server position for movement interpolation
    this.serverPosition = { ...this.position };
    this.hasReceivedPosition = false;
    this.lastServerPositionUpdate = Date.now();
    
    // Enemy stats
    this.health = config.health || 100;
    this.maxHealth = config.maxHealth || config.health || 100;
    this.level = config.level || 1;
    
    // Use server-provided speed if available, with a reasonable default and minimum
    this.speed = typeof config.speed === 'number' ? 
      Math.max(config.speed, 2) : // Ensure minimum speed of 2
      3; // Default speed if none provided
    
    // Boss properties
    this.isBoss = config.isBoss === true;
    this.scale = config.scale || (this.isBoss ? 3.0 : 1.0);
    
    // Track model scaling state
    this.scalingApplied = false;
    
    if (this.isBoss) {
      console.log(`👹 Boss enemy ${this.id} created with scale: ${this.scale}x and health: ${this.health}`);
      // Boss enemies move slightly slower but hit harder
      this.attackDamage = this.health * 0.2; // Higher damage
      this.attackRange = 3.0; // Longer attack range
      
      // Create a timer to verify the boss status is maintained
      this._setupBossVerification();
    } else {
      console.log(`Enemy ${this.id} created with speed: ${this.speed}`);
    }
    
    // AI and state variables
    this.targetPlayer = null;
    this.state = 'idle'; // idle, patrolling, chasing, attacking
    this.lastUpdateTime = Date.now();
    this.lastAttackTime = 0;
    this.attackCooldown = 2000;
    
    // Track creation time
    this.createdTime = Date.now();
    
    // Animation state
    this.currentAnimation = 'idle'; // Start in idle animation until freeze is over
    
    // References to mesh and other visual elements
    this.mesh = null;
    
    // Projectile settings for ranged enemies
    this.isRanged = this.type === 'ranged' || this.type === 'mage';
    this.projectileType = this.type === 'mage' ? 'magic' : 'arrow';
    this.projectileSpeed = 10; // Units per second
    this.projectileColor = this.type === 'mage' ? 0x00bbff : 0xff3300; // Blue for mage, red for rogue
    
    // Track server-provided distance to target
    this.serverDistanceToTarget = null;
    this.lastServerDistanceUpdate = 0;
    
    // CRITICAL FIX: Define attack range per enemy type 
    this.attackRange = this.isRanged ? 
      (this.type === 'mage' ? 9.5 : 8.0) : // Different ranges for mage vs ranged
      2.5; // Melee range
    
    // Track last facing direction to avoid rapid changes
    this.lastFacingDirection = { x: 0, z: 1 }; // Default facing forward
    this.lastFacingUpdateTime = 0;
    this.facingChangeCooldown = 200; // Milliseconds between direction changes

    // Initialize with active state
    this.isActive = true;
    
    // CRITICAL FIX: Add stabilization mode properties for new enemies
    this.creationTime = Date.now();
    this.isStabilizing = true;  // Start in stabilization mode
    
    // NEW: Add a complete freeze flag during initial spawn (2 seconds)
    this.isFrozen = true;
    this.frozenUntil = this.creationTime + 2000; // Freeze for 2 seconds
    this.positionBeforeFreeze = { ...this.position }; // Store position before freeze
    this.targetBeforeFreeze = null; // Store target before freeze
    
    // Track state change time to prevent getting stuck in states
    this.lastStateChangeTime = Date.now();
    
    // Buffer for position updates after freeze ends
    this.pendingServerPositions = [];
    this.maxPendingPositions = 5;
    
    // Initialize position buffer for position smoothing during stabilization
    this.positionBuffer = [];
    
    // Visual effect pooling
    this.attackEffectPool = [];
    this.maxAttackEffects = 5;
    this.currentAttackEffects = 0;
    this.effectReuseDelay = 1000; // Minimum time before reusing an effect
    
    console.log(`Enemy ${this.id} created: ${this.type} (Level ${this.level}) with attack range ${this.attackRange}`);
  }

  /**
   * Sets up a periodic check to ensure boss properties are maintained
   * @private
   */
  _setupBossVerification() {
    if (!this.isBoss) return;
    
    // Check and reapply boss properties after a short delay and periodically
    setTimeout(() => {
      this._verifyBossProperties();
      
      // Set up periodic verification
      this._bossVerificationInterval = setInterval(() => {
        this._verifyBossProperties();
      }, 5000); // Check every 5 seconds
    }, 1000);
  }
  
  /**
   * Verifies and reapplies boss properties if needed
   * @private
   */
  _verifyBossProperties() {
    if (!this.isBoss) return;
    
    // Log if this is the first verification
    if (!this.bossVerified) {
      console.log(`👹 Verifying boss properties for enemy ${this.id}`);
      this.bossVerified = true;
    }
    
    // Ensure scale property is set
    if (!this.scale || this.scale < 2.0) {
      console.warn(`👹 Boss enemy ${this.id} has invalid scale ${this.scale}, resetting to 3.0`);
      this.scale = 3.0;
    }
    
    // Apply scale to mesh if available
    if (this.mesh && !this.scalingApplied) {
      console.log(`👹 Applying scale ${this.scale} to boss enemy mesh`);
      this.mesh.scale.set(this.scale, this.scale, this.scale);
      this.scalingApplied = true;
    }
    
    // If boss was created with low health, boost it
    if (this.maxHealth < 100) {
      const newHealth = this.health * 3;
      const newMaxHealth = this.maxHealth * 3;
      console.log(`👹 Boosting boss enemy health: ${this.health} → ${newHealth}`);
      this.health = newHealth;
      this.maxHealth = newMaxHealth;
    }
  }

  /**
   * Update enemy state and position
   * @param {Object} players - Available players for targeting
   * @param {Object} projectileManager - Optional projectile manager for ranged attacks
   */
  update(players, projectileManager = null) {
    if (!players || Object.keys(players).length === 0) {
      return; // No players to interact with
    }
    
    // CRITICAL FIX: Check if this enemy is active
    if (!this.isActive) {
      // Re-activate this enemy if it's been inactive for a while
      if (Date.now() - this.lastUpdateTime > 5000) {
        console.log(`Re-activating inactive enemy ${this.id}`);
        this.isActive = true;
      } else {
        return; // Skip update for inactive enemies
      }
    }
    
    try {
      const now = Date.now();
      const deltaTime = (now - this.lastUpdateTime) / 1000; // in seconds
      this.lastUpdateTime = now;
      
      // ENHANCED: Detect if enemy has been stuck in the same state for too long
      const timeInCurrentState = now - this.lastStateChangeTime;
      if (timeInCurrentState > 10000) { // 10 seconds in the same state
        console.log(`Enemy ${this.id} has been in state "${this.state}" for ${timeInCurrentState}ms - forcing state change`);
        
        // Force change to moving/walking state
        this.state = 'moving';
        this.currentAnimation = 'walk';
        this.lastStateChangeTime = now;
      }
      
      // CRITICAL FIX: Check if frozen period has ended
      if (this.isFrozen && now > this.frozenUntil) {
        this.isFrozen = false;
        console.log(`Enemy ${this.id} freeze period ended - starting movement`);
        
        // Change animation from idle to walk
        this.currentAnimation = 'walk';
        
        // If we have pending server positions, apply the most recent one
        if (this.pendingServerPositions.length > 0) {
          // Take the most recent server position 
          const latestPos = this.pendingServerPositions[this.pendingServerPositions.length - 1];
          this.serverPosition = latestPos;
          console.log(`Applying stored server position after freeze: (${latestPos.x.toFixed(2)}, ${latestPos.z.toFixed(2)})`);
        }
      }
      
      // CRITICAL FIX: If enemy is frozen, don't do any movement updates at all,
      // just target finding
      if (this.isFrozen) {
        // During freeze, only find target but don't move
        this.findNearestPlayer(players);
        
        // Store the target player for when freeze ends
        if (this.targetPlayer && !this.targetBeforeFreeze) {
          this.targetBeforeFreeze = { ...this.targetPlayer };
        }
        
        // If server sent position updates during freeze, store them
        if (this.serverPosition && this.serverPosition !== this.initialPosition) {
          // Add to pending positions if different enough from last one
          const lastPending = this.pendingServerPositions.length > 0 ? 
            this.pendingServerPositions[this.pendingServerPositions.length - 1] : null;
          
          if (!lastPending || 
              Math.abs(lastPending.x - this.serverPosition.x) > 0.1 || 
              Math.abs(lastPending.z - this.serverPosition.z) > 0.1) {
            // Only keep a limited number of positions
            if (this.pendingServerPositions.length >= this.maxPendingPositions) {
              this.pendingServerPositions.shift(); // Remove oldest
            }
            
            // Store new position
            this.pendingServerPositions.push({ ...this.serverPosition });
          }
          
          // Reset server position to initial during freeze
          this.serverPosition = { ...this.initialPosition };
        }
        
        // Update mesh position with frozen position and facing
        this.updateMeshPosition();
        return;
      }
      
      // CRITICAL FIX: Check if stabilization period is over
      if (this.isStabilizing && now > this.frozenUntil + 3000) { // 3 seconds after freeze
        this.isStabilizing = false;
        // Track this state change
        this.lastStateChangeTime = now;
        console.log(`Enemy ${this.id} completed stabilization period`);
      }
      
      // DEBUG: Log enemy position update occasionally
      const shouldLog = Math.random() < 0.005; // Only log 0.5% of the time to avoid spamming
      
      // Check if this is a newly spawned enemy 
      const isNewEnemy = (now - this.creationTime) < 5000; // Less than 5 seconds old
      const isVeryNewEnemy = (now - this.creationTime) < 3000; // Just out of freeze (3 seconds)
      
      // More aggressive logging for new enemies
      const shouldLogNewEnemy = isNewEnemy && Math.random() < 0.05; // 5% chance for new enemies
      
      if (shouldLogNewEnemy) {
        console.log(`New enemy ${this.id} update at ${now - this.creationTime}ms old`);
        console.log(`  Position: (${this.position.x.toFixed(2)}, ${this.position.z.toFixed(2)})`);
        console.log(`  Server position: ${this.serverPosition ? `(${this.serverPosition.x.toFixed(2)}, ${this.serverPosition.z.toFixed(2)})` : 'none'}`);
        console.log(`  State: ${this.state}, Animation: ${this.currentAnimation}`);
      }
      
      // ENHANCED: Ensure we always find a target player regardless of enemy state
      // Moving this before other state management makes targeting more reliable
      const foundTarget = this.findNearestPlayer(players);
      
      // If we still don't have a target, but have valid players, try harder
      if (!this.targetPlayer && Object.keys(players).length > 0) {
        // Desperate attempt to find ANY valid player
        for (const playerId in players) {
          const player = players[playerId];
          if (player && player.position && 
              typeof player.position.x === 'number' && 
              typeof player.position.z === 'number') {
            // Log this desperate assignment
            console.log(`Enemy ${this.id} desperate target assignment to ${playerId}`);
            this.targetPlayer = {
              id: playerId,
              position: { ...player.position }
            };
            break;
          }
        }
      }
      
      // Get current distance to target if we have one
      const distanceToTarget = this.targetPlayer ? this.getDistanceToTarget() : Infinity;
      
      if (shouldLog || shouldLogNewEnemy) {
        console.log(`Enemy ${this.id} - Distance to target: ${distanceToTarget.toFixed(2)}, State: ${this.state}`);
      }
      
      // ENHANCED: Prioritize server distance for attack decisions to better sync with server
      const useServerDistance = this.serverDistanceToTarget !== null && 
                               (now - this.lastServerDistanceUpdate < 500); // Only trust server distance if recent
      
      const effectiveDistance = useServerDistance ? 
        parseFloat(this.serverDistanceToTarget) : distanceToTarget;
      
      // CRITICAL FIX: If in attack state but too far from target, force exit
      if (this.targetPlayer) {
        const maxAttackRange = this.attackRange;
        
        // If we're way out of range, force exit attack state regardless of animations
        if (effectiveDistance > maxAttackRange * 1.5 && this.state === 'attacking') {
          this.state = 'chasing';
          this.currentAnimation = 'walk';
          this.lastStateChangeTime = now; // Track this state change
          
          if (shouldLog || shouldLogNewEnemy) {
            console.log(`Enemy ${this.id} force exit attack state - player too far (${effectiveDistance.toFixed(2)})`);
          }
        }
      }
      
      // For new enemies, always prioritize movement
      if (isNewEnemy && this.state !== 'attacking') {
        this.state = 'moving';
        this.currentAnimation = 'walk';
      }
      
      // Only lock animation for a short time to prevent getting stuck
      const attackAnimationInProgress = this.currentAnimation === 'attack' && 
                                      (now - this.lastAttackTime < 400); // Reduced from 600ms to 400ms
      
      if (attackAnimationInProgress && !isNewEnemy) { // Don't lock new enemies in attack animations
        // Still update position during attack animation
        if (this.serverPosition) {
          // Get distance to server position
          const distanceToServer = Math.sqrt(
            Math.pow(this.position.x - this.serverPosition.x, 2) +
            Math.pow(this.position.z - this.serverPosition.z, 2)
          );
          
          // Only interpolate for small distances; prevents "skating" while attacking
          if (distanceToServer < 1.0) {
            // Use gentle LERP during attack
            const t = Math.min(deltaTime, 0.05);
            this.position.x += (this.serverPosition.x - this.position.x) * t;
            this.position.z += (this.serverPosition.z - this.position.z) * t;
          }
        }
        
        // Update mesh position and facing
        this.updateMeshPosition();
        return;
      }
      
      // ENHANCED: Always update position when we have a server position
      // This ensures enemies keep moving even if they get "stuck"
      if (this.serverPosition) {
        // Always set hasReceivedPosition to true once we have serverPosition
        this.hasReceivedPosition = true;
        
        // Check if we need to move towards server position
        const distanceToServer = Math.sqrt(
          Math.pow(this.position.x - this.serverPosition.x, 2) +
          Math.pow(this.position.z - this.serverPosition.z, 2)
        );
        
        if (shouldLog || shouldLogNewEnemy) {
          console.log(`Enemy ${this.id} - Distance to server position: ${distanceToServer.toFixed(2)}`);
          console.log(`  Current: (${this.position.x.toFixed(2)}, ${this.position.z.toFixed(2)})`);
          console.log(`  Server: (${this.serverPosition.x.toFixed(2)}, ${this.serverPosition.z.toFixed(2)})`);
        }
        
        // Skip position updates more frequently during stabilization
        if (this.isStabilizing) {
          if ((now - (this.lastPositionUpdateTime || 0)) < 200) {
            if (shouldLogNewEnemy) {
              console.log(`Enemy ${this.id} skipping position update during stabilization period`);
            }
            
            // Update mesh position and facing
            this.updateMeshPosition();
            return;
          }
          
          this.lastPositionUpdateTime = now;
        }
        
        // ENHANCED: Always move if distance is significant or if enemy has been inactive
        const inactiveForTooLong = now - (this.lastMoveTime || 0) > 2000; // No movement for 2 seconds
        const shouldMove = distanceToServer > 0.05 || isNewEnemy || inactiveForTooLong;
        
        if (shouldMove) {
          // For stabilizing enemies, use position smoothing
          if (this.isStabilizing) {
            // Add current server position to buffer for averaging
            this.positionBuffer.push({ ...this.serverPosition });
            
            // Keep buffer size limited
            if (this.positionBuffer.length > 3) {
              this.positionBuffer.shift(); // Remove oldest position
            }
            
            // Average positions in buffer to get smoother target
            let avgX = 0, avgZ = 0;
            this.positionBuffer.forEach(pos => {
              avgX += pos.x;
              avgZ += pos.z;
            });
            avgX /= this.positionBuffer.length;
            avgZ /= this.positionBuffer.length;
            
            // Use the averaged position as the target
            const avgPos = { x: avgX, z: avgZ };
            
            // Use more direct movement during stabilization
            const t = 0.2; // Fixed interpolation factor during stabilization
            
            this.position.x += (avgPos.x - this.position.x) * t;
            this.position.z += (avgPos.z - this.position.z) * t;
            
            if (shouldLogNewEnemy) {
              console.log(`  Stabilization movement with fixed factor ${t.toFixed(3)}`);
              console.log(`  Averaged position: (${avgPos.x.toFixed(2)}, ${avgPos.z.toFixed(2)})`);
              console.log(`  New position: (${this.position.x.toFixed(2)}, ${this.position.z.toFixed(2)})`);
            }
          } 
          // Normal movement for non-stabilizing enemies
          else {
            // Calculate interpolation factor - adjust based on context
            let t;
            
            // Very new enemies move more directly
            if (isVeryNewEnemy) {
              t = 0.5; // Direct movement at first
            }
            // New but not very new enemies move fast but with interpolation
            else if (isNewEnemy) {
              t = Math.min(3.0 * deltaTime, 0.15);
            } 
            // If inactive for too long, use more aggressive interpolation
            else if (inactiveForTooLong) {
              t = Math.min(6.0 * deltaTime, 0.3); // More aggressive LERP to catch up
              console.log(`Enemy ${this.id} using catch-up interpolation after inactivity`);
            }
            // Regular distance-based interpolation
            else if (distanceToServer > 10) {
              t = Math.min(1.0 * deltaTime, 0.05);
            } else if (distanceToServer > 5) {
              t = Math.min(2.0 * deltaTime, 0.08);
            } else {
              t = Math.min(2.5 * deltaTime, 0.12);
            }
            
            // Apply LERP to x and z coordinates
            this.position.x += (this.serverPosition.x - this.position.x) * t;
            this.position.z += (this.serverPosition.z - this.position.z) * t;
            
            // Track when we last moved
            this.lastMoveTime = now;
            
            if (shouldLog || shouldLogNewEnemy) {
              console.log(`  Moving with LERP factor ${t.toFixed(3)} based on distance ${distanceToServer.toFixed(2)}`);
              console.log(`  New position: (${this.position.x.toFixed(2)}, ${this.position.z.toFixed(2)})`);
            }
          }
          
          // For new enemies or if not in attack state, update animation to walk
          if (isNewEnemy || (this.state !== 'attacking' && this.currentAnimation !== 'attack')) {
            this.currentAnimation = 'walk';
            this.state = 'moving';
          }
        } else if (shouldLog) {
          console.log(`Enemy ${this.id} - Not moving, distance to server position too small: ${distanceToServer.toFixed(3)}`);
        }
      } else if (shouldLog || shouldLogNewEnemy) {
        console.log(`Enemy ${this.id} - No server position available`);
      }
      
      // For new enemies, prioritize movement and skip attack states
      if (isNewEnemy) {
        // Ensure we're in moving state
        this.state = 'moving';
        this.currentAnimation = 'walk';
        
        // Move toward target if we have one
        if (this.targetPlayer) {
          this.moveTowardsTarget(deltaTime * 2); // Move faster when new
        }
        
        // Update mesh and finish early
        this.updateMeshPosition();
        return;
      }
      
      // Regular update logic for local prediction and AI
      // Update based on current state - only if we're not directly controlled by server
      if (this.state === 'attacking') {
        // In attack state, manage attack cooldown and animation
        const canAttack = now - this.lastAttackTime > this.attackCooldown;
        
        if (canAttack) {
          // Start a new attack
          this.currentAnimation = 'attack';
          this.lastAttackTime = now; // Track when attack started
          
          // Create an independent attack attempt with unique ID
          const attackTimeId = Date.now() + Math.floor(Math.random() * 1000);
          
          // Perform attack after a small random delay to prevent exact simultaneity
          setTimeout(() => {
            // Recheck canAttack to prevent double attacks from setTimeout race conditions
            if (Date.now() - this.lastAttackTime >= this.attackCooldown/2) {
              // Perform actual attack with unique ID
              if (this.isRanged && projectileManager) {
                this.rangedAttack(projectileManager);
              } else {
                this.meleeAttack();
              }
            }
          }, this.id.length % 10); // Small staggered delay (0-9ms) based on ID length
        }
        
        // Check if player moved out of range more aggressively
        // This prevents the "stuck in attack animation" issue
        const attackRange = this.attackRange;
        
        // Force check if player is out of range more often
        if (!this.targetPlayer || effectiveDistance > attackRange * 1.5) {
          this.state = 'chasing';
          this.currentAnimation = 'walk'; // Always switch to walk when exiting attack state
          this.lastStateChangeTime = now; // Track state change time
          
          if (shouldLog) {
            console.log(`Enemy ${this.id} exited attack state - target out of range or lost`);
          }
          
          // Force update to walk animation
          if (this.mesh && this.mesh.userData) {
            this.mesh.userData.forceWalkAnimation = true;
          }
        }
        
        // If we've been in attack state for too long, force exit
        // This prevents enemies from getting stuck in attack mode
        const attackStateDuration = now - this.lastStateChangeTime;
        if (attackStateDuration > 5000) {  // If in attack state for more than 5 seconds
          console.log(`Enemy ${this.id} force exiting attack state after ${attackStateDuration}ms`);
          this.state = 'chasing';
          this.currentAnimation = 'walk';
          this.lastStateChangeTime = now;
        }
      }
      // Other states (idle, patrolling, chasing) handled normally
      else {
      switch (this.state) {
        case 'idle':
          // 5% chance to start patrolling each update
          if (Math.random() < 0.05) {
            this.state = 'patrolling';
            this.lastStateChangeTime = now; // Track state change
          }
          // Set animation to walk for idle/patrolling
          this.currentAnimation = 'walk';
          break;
          
        case 'patrolling':
          this.patrol(deltaTime);
          
          // If we have a target player in range, start chasing
          if (this.targetPlayer && this.getDistanceToTarget() < 10) {
            // Track state change time
            if (this.state !== 'chasing') {
              this.lastStateChangeTime = now;
            }
            this.state = 'chasing';
          }
          // Set animation to walk for patrolling
          this.currentAnimation = 'walk';
          break;
          
        case 'chasing':
          if (this.targetPlayer) {
              // Only move toward player if server position isn't directing us elsewhere
              if (!this.serverPosition || Math.sqrt(
                Math.pow(this.position.x - this.serverPosition.x, 2) +
                Math.pow(this.position.z - this.serverPosition.z, 2)
              ) < 0.1) {
            this.moveTowardsTarget(deltaTime);
              }
            
            // If close enough to attack
              const attackRange = this.attackRange;
              if (effectiveDistance < attackRange) {
                // Track state change time
                if (this.state !== 'attacking') {
                  this.lastStateChangeTime = now;
                }
                
              this.state = 'attacking';
                // Set attack animation immediately and track start time
                this.currentAnimation = 'attack';
                this.lastAttackTime = now;
                
                // If we just transitioned to attack state, log it
                if (shouldLog) {
                  console.log(`Enemy ${this.id} entered attack range of player at distance ${effectiveDistance.toFixed(2)}`);
                }
            }
          } else {
            this.state = 'patrolling';
            this.lastStateChangeTime = now; // Track state change
          }
          // Set animation to walk for chasing
          if (this.state !== 'attacking') {
            this.currentAnimation = 'walk';
          }
          break;
          
        // ENHANCED: Add a generic moving state for better server sync
        case 'moving':
          // If we have a target, transition to chasing
          if (this.targetPlayer) {
            this.state = 'chasing';
            this.lastStateChangeTime = now;
          } else {
            // If we've been in moving state too long with no target, go to patrolling
            const movingStateDuration = now - this.lastStateChangeTime;
            if (movingStateDuration > 3000) {
              this.state = 'patrolling';
              this.lastStateChangeTime = now;
            }
          }
          this.currentAnimation = 'walk';
          break;
        }
      }
      
      // Update mesh position and facing direction
      this.updateMeshPosition();
    } catch (error) {
      console.error(`Error updating enemy ${this.id}:`, error);
    }
  }

  /**
   * Set distance to target from server
   * This allows the client to use server-provided distances for consistency
   * @param {string} distance - Distance as string from server
   */
  setServerDistanceToTarget(distance) {
    if (distance !== undefined) {
      this.serverDistanceToTarget = distance;
      this.lastServerDistanceUpdate = Date.now();
    }
  }

  /**
   * Update the mesh position and facing direction
   * Centralized method to ensure consistent updates
   */
  updateMeshPosition() {
    if (this.mesh) {
      // Update position
      this.mesh.position.set(this.position.x, this.position.y, this.position.z);
      
      // Update facing direction
      this.updateFacing();
    }
  }

  /**
   * Perform a melee attack
   */
  meleeAttack() {
    if (this.isDead || !this.targetPlayer) return;
    
    // Check if we can create a new attack effect
    if (this.currentAttackEffects >= this.maxAttackEffects) {
      return;
    }
    
    // Create or reuse attack effect
    const effect = this.createAttackEffect();
    if (effect) {
      this.currentAttackEffects++;
      
      // Remove effect after animation
      setTimeout(() => {
        if (effect.parent) {
          effect.parent.remove(effect);
        }
        this.currentAttackEffects--;
      }, this.attackAnimationDuration);
    }
    
    // Update last attack time
    this.lastAttackTime = Date.now();
  }

  createAttackEffect() {
    // Try to reuse an existing effect from the pool
    let effect = this.attackEffectPool.find(e => !e.parent && 
      (Date.now() - e.lastUseTime) > this.effectReuseDelay);
    
    if (!effect) {
      // Create new effect if pool is empty
      effect = new THREE.Mesh(
        new THREE.SphereGeometry(0.3, 8, 8),
        new THREE.MeshBasicMaterial({ 
          color: 0xff0000, 
          transparent: true, 
          opacity: 0.8 
        })
      );
      this.attackEffectPool.push(effect);
    }
    
    // Position effect at target
    effect.position.copy(this.targetPlayer.position);
    effect.position.y += 1;
    
    // Add to scene
    scene.add(effect);
    
    // Track last use time
    effect.lastUseTime = Date.now();
    
    // Animate with optimized parameters
    const startScale = 0.5;
    const endScale = 2.0;
    const duration = 500;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const scale = startScale + (endScale - startScale) * progress;
      effect.scale.set(scale, scale, scale);
      
      effect.material.opacity = 0.8 * (1 - progress);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
    
    return effect;
  }

  /**
   * Perform a ranged attack by spawning a projectile
   * @param {Object} projectileManager - The projectile manager to spawn projectiles
   */
  rangedAttack(projectileManager) {
    if (this.isDead || !this.targetPlayer || !projectileManager) return;
    
    // Check if we can create a new attack effect
    if (this.currentAttackEffects >= this.maxAttackEffects) {
      return;
    }
    
    // Create or reuse projectile effect
    const effect = this.createProjectileEffect(projectileManager);
    if (effect) {
      this.currentAttackEffects++;
      
      // Remove effect after animation
      setTimeout(() => {
        if (effect.parent) {
          effect.parent.remove(effect);
        }
        this.currentAttackEffects--;
      }, this.attackAnimationDuration);
    }
    
    // Update last attack time
    this.lastAttackTime = Date.now();
  }

  createProjectileEffect(projectileManager) {
    // Try to reuse an existing effect from the pool
    let effect = this.attackEffectPool.find(e => !e.parent && 
      (Date.now() - e.lastUseTime) > this.effectReuseDelay);
    
    if (!effect) {
      // Create new effect if pool is empty
      effect = new THREE.Mesh(
        new THREE.SphereGeometry(0.2, 8, 8),
        new THREE.MeshBasicMaterial({ 
          color: this.projectileColor,
          transparent: true,
          opacity: 0.8
        })
      );
      this.attackEffectPool.push(effect);
    }
    
    // Position effect at enemy
    effect.position.copy(this.position);
    effect.position.y += 1;
    
    // Add to scene
    scene.add(effect);
    
    // Track last use time
    effect.lastUseTime = Date.now();
    
    // Animate with optimized parameters
    const startPos = effect.position.clone();
    const targetPos = this.targetPlayer.position.clone();
    targetPos.y += 1;
    const duration = 1000;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Linear interpolation for position
      effect.position.lerpVectors(startPos, targetPos, progress);
      
      // Scale and opacity animation
      const scale = 0.5 + progress * 0.5;
      effect.scale.set(scale, scale, scale);
      effect.material.opacity = 0.8 * (1 - progress);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
    
    return effect;
  }

  /**
   * Find the nearest player to target
   * @param {Object} players - Available players
   * @returns {boolean} - True if a target was found
   */
  findNearestPlayer(players) {
    let nearestDistance = Infinity;
    let nearestPlayer = null;
    
    // DEBUG: Track target acquisition
    const oldTargetId = this.targetPlayer ? this.targetPlayer.id : null;
    
    // Find the nearest player
    Object.values(players).forEach(player => {
      if (player && player.position) {
        // Skip players with invalid positions
        if (typeof player.position.x !== 'number' || 
            typeof player.position.z !== 'number') {
          return;
        }
        
        const distance = this.getDistanceTo(player.position);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestPlayer = player;
        }
      }
    });
    
    // Only update target if we found a valid player
    if (nearestPlayer) {
      // Check if target changed - this helps debug targeting issues
      if (!this.targetPlayer || this.targetPlayer.id !== nearestPlayer.id) {
        console.log(`Enemy ${this.id} changing target from ${oldTargetId} to ${nearestPlayer.id} at distance ${nearestDistance.toFixed(2)}`);
      } else {
        // Always update target position even if it's the same target
        // This ensures enemies always have the latest player position
        this.targetPlayer.position = { ...nearestPlayer.position };
      }
      
      // Store a COPY of the player data to prevent reference sharing between enemies
      if (!this.targetPlayer || this.targetPlayer.id !== nearestPlayer.id) {
        this.targetPlayer = {
          id: nearestPlayer.id,
          position: { ...nearestPlayer.position }
        };
      }
      
      return true;
    }
    
    return false;
  }

  /**
   * Calculate distance to a position
   * @param {Object} position - Position to measure to
   * @returns {number} - Distance
   */
  getDistanceTo(position) {
    if (!position) return Infinity;
    
    return Math.sqrt(
      Math.pow(this.position.x - position.x, 2) +
      Math.pow(this.position.y - position.y, 2) +
      Math.pow(this.position.z - position.z, 2)
    );
  }

  /**
   * Calculate distance to the current target player
   * @returns {number} - Distance to target or Infinity if no target
   */
  getDistanceToTarget() {
    if (!this.targetPlayer || !this.targetPlayer.position) return Infinity;
    
    return this.getDistanceTo(this.targetPlayer.position);
  }

  /**
   * Move in a random patrol pattern
   * @param {number} deltaTime - Time since last update
   */
  patrol(deltaTime) {
    // Simple random movement for now
    this.position.x += (Math.random() - 0.5) * this.speed * deltaTime;
    this.position.z += (Math.random() - 0.5) * this.speed * deltaTime;
  }

  /**
   * Move towards the target player
   * @param {number} deltaTime - Time since last update
   */
  moveTowardsTarget(deltaTime) {
    if (!this.targetPlayer || !this.targetPlayer.position) return;
    
    const target = this.targetPlayer.position;
    const directionX = target.x - this.position.x;
    const directionZ = target.z - this.position.z;
    
    // Normalize direction
    const length = Math.sqrt(directionX * directionX + directionZ * directionZ);
    if (length > 0) {
      const normalizedX = directionX / length;
      const normalizedZ = directionZ / length;
      
      // Move in the calculated direction
      this.position.x += normalizedX * this.speed * deltaTime;
      this.position.z += normalizedZ * this.speed * deltaTime;
    }
  }

  /**
   * Update mesh direction to face the correct target
   * CRITICAL FIX: This is completely rewritten to ensure independent facing
   * for each enemy and prevent direction sharing
   */
  updateFacing() {
    if (!this.mesh || !this.mesh.lookAt) return;
    
    const now = Date.now();
    
    // Check if this is a newly spawned enemy
    const isNewEnemy = (now - (this.creationTime || 0)) < 5000;
    
    // CRITICAL FIX: During stabilization period, update facing less frequently
    if (this.isStabilizing && (now - this.lastFacingUpdateTime < 500)) {
      return; // Update facing direction much less frequently during stabilization
    }
    
    // For stabilized but new enemies, update facing more frequently
    if (!this.isStabilizing && !isNewEnemy && now - this.lastFacingUpdateTime < this.facingChangeCooldown) {
      return;
    }
    
    let lookTarget = null;
    let targetSource = '';
    
    // CRITICAL FIX: For new enemies, prioritize target player over server position
    // This ensures they face the right way from the beginning
    if (isNewEnemy && this.targetPlayer && this.targetPlayer.position) {
      lookTarget = {
        x: this.targetPlayer.position.x,
        y: this.position.y,
        z: this.targetPlayer.position.z
      };
      targetSource = 'initial-player';
    }
    // Otherwise follow normal priority
    else {
      // Priority 1: If we're moving toward server position and it's different enough, look that way
      if (this.serverPosition && (this.state === 'moving' || this.state === 'chasing')) {
        const distanceToServer = Math.sqrt(
          Math.pow(this.position.x - this.serverPosition.x, 2) +
          Math.pow(this.position.z - this.serverPosition.z, 2)
        );
        
        if (distanceToServer > 0.2) { // Only change direction for meaningful distances
          lookTarget = { 
            x: this.serverPosition.x, 
            y: this.position.y, 
            z: this.serverPosition.z 
          };
          targetSource = 'server';
        }
      }
      
      // Priority 2: If attacking or chasing and we have a target, look at them
      if (!lookTarget && this.targetPlayer && this.targetPlayer.position && 
          (this.state === 'attacking' || this.state === 'chasing')) {
        // Calculate vector to target
        const targetVector = {
          x: this.targetPlayer.position.x - this.position.x,
          z: this.targetPlayer.position.z - this.position.z
        };
        
        // Only use this direction if it's a significant change
        const length = Math.sqrt(targetVector.x * targetVector.x + targetVector.z * targetVector.z);
        
        if (length > 0.1) { // Avoid tiny movements
          lookTarget = {
            x: this.targetPlayer.position.x,
            y: this.position.y,
            z: this.targetPlayer.position.z
          };
          targetSource = 'player';
        }
      }
    }
    
    // If we have a target to look at, apply it with smoothing
    if (lookTarget) {
      // Create a smoothed target by blending old and new directions
      // This prevents "snapping" and makes movement more natural
      
      // CRITICAL FIX: Use more aggressive blending for new enemies,
      // but use smoother blending during stabilization period
      let blendFactor;
      if (this.isStabilizing) {
        blendFactor = 0.5; // More gentle during stabilization
      } else if (isNewEnemy) {
        blendFactor = 0.95; // Very responsive for new enemies
      } else {
        blendFactor = 0.8; // Normal smoothing for established enemies
      }
      
      // Current facing direction
      const currentDir = this.lastFacingDirection;
      
      // New direction vector
      const newDir = {
        x: lookTarget.x - this.position.x,
        z: lookTarget.z - this.position.z
      };
      
      // Normalize new direction
      const newLength = Math.sqrt(newDir.x * newDir.x + newDir.z * newDir.z);
      if (newLength > 0) {
        newDir.x /= newLength;
        newDir.z /= newLength;
        
        // Blend old and new directions
        const smoothedDir = {
          x: currentDir.x * (1 - blendFactor) + newDir.x * blendFactor,
          z: currentDir.z * (1 - blendFactor) + newDir.z * blendFactor
        };
        
        // Normalize the result
        const smoothedLength = Math.sqrt(smoothedDir.x * smoothedDir.x + smoothedDir.z * smoothedDir.z);
        if (smoothedLength > 0) {
          smoothedDir.x /= smoothedLength;
          smoothedDir.z /= smoothedLength;
          
          // Calculate world position to look at
          const lookPosition = {
            x: this.position.x + smoothedDir.x,
            y: this.position.y,
            z: this.position.z + smoothedDir.z
          };
          
          // CRITICAL FIX: For new enemies, immediately face toward the player
          // instead of gradually turning to avoid the initial "wrong facing" issue
          if (isNewEnemy && targetSource === 'initial-player' && !this.isStabilizing) {
            this.mesh.lookAt(lookTarget.x, lookTarget.y, lookTarget.z);
            
            // Still update the smoothed direction for future updates
            this.lastFacingDirection = {
              x: newDir.x,
              z: newDir.z
            };
          } else {
            // Actually look at the target with smooth transition
            this.mesh.lookAt(lookPosition.x, lookPosition.y, lookPosition.z);
            
            // Save this direction as our last facing direction
            this.lastFacingDirection = smoothedDir;
          }
          
          this.lastFacingUpdateTime = now;
          
          // Occasionally log the facing direction
          if (Math.random() < 0.01) {
            const targetPos = lookTarget;
            console.log(`Enemy ${this.id} facing direction: (${targetPos.x.toFixed(2)}, ${targetPos.z.toFixed(2)}) [${targetSource}]`);
          }
        }
      }
    }
  }

  /**
   * Mark this enemy as active or inactive
   * Useful for debugging and fixing stuck enemies
   * @param {boolean} active - Whether enemy should be active
   */
  setActive(active) {
    this.isActive = active;
    
    if (active) {
      console.log(`Enemy ${this.id} has been activated`);
      this.lastUpdateTime = Date.now();
    } else {
      console.log(`Enemy ${this.id} has been deactivated`);
    }
  }

  /**
   * Take damage from an attack
   * @param {number} amount - Amount of damage to take
   * @param {Object} source - Source of the damage (player, etc)
   * @returns {boolean} - True if the enemy is still alive
   */
  takeDamage(amount, source) {
    if (amount <= 0) return true;
    
    this.health -= amount;
    console.log(`Enemy ${this.id} took ${amount} damage from ${source?.id || 'unknown'}, health: ${this.health}/${this.maxHealth}`);
    
    if (this.health <= 0) {
      this.die();
      return false;
    }
    
    // If we're not already chasing or attacking, start chasing the attacker
    if (this.state !== 'chasing' && this.state !== 'attacking') {
      this.targetPlayer = source;
      this.state = 'chasing';
    }
    
    return true;
  }

  /**
   * Handle enemy death
   */
  die() {
    console.log(`Enemy ${this.id} has been defeated!`);
    // Actual cleanup happens in the enemy manager
  }
}

// Make compatible with CommonJS
if (typeof module !== 'undefined') {
  module.exports = Enemy;
} 